import React, { useContext, useEffect, useState } from 'react';
import { FaBold, FaItalic } from 'react-icons/fa';
import { UserContext } from '../../../context/UserContext';
import { Input, Label } from '../../../StyledComponents/Input/generic';
import { ModalCard, ModalCardbody } from '../../../StyledComponents/Modal/generic';
import { CheckboxContainer, DropzoneContainer, PreviewColor, PrintTemplateModalContainer, ResizeContainer } from '../FormStyle';
import { ColorResult, SketchPicker } from 'react-color';
import { useTranslation } from 'react-i18next';
import Clients, { ClientType } from '../../../service/Clients';
import AttToken from '../../../helpers/attToken';
import PrintService, { printTemplate } from '../../../service/PrintTemplate';
import { PrintTemplateType } from '../Types';
import { toast } from 'react-toastify';
import PrintPreviewCreateModal from './PrintPreviewCreateModal';
import queryClient from '../../../service/query';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { Autocomplete, Button, Skeleton, TextField } from '@mui/material';
import { trackEventMatomo } from '../../../helpers/matomo';
import QueryFn from '../../../helpers/QueryFn';
import HeaderModal from '../../HeaderModal/HeaderModal';
import FooterModal from '../../FooterModal/FooterModal';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';

const queryFn = new QueryFn();
const customerService = new Clients();
const printService = new PrintService();

const initialState = {
  name: 'Novo template',
  dafaultTemplate: false,
  cliente: undefined,
  headerText: {
    bold: false,
    italic: false,
    size: 10,
    text: undefined,
  },
  footerText: {
    bold: false,
    italic: false,
    size: 10,
    text: undefined,
  },
  color: undefined,
  logo: undefined,
  status: true,
};

export default function PrintTemplateModal({ editing, setEditing, selectedPrint }
  : { editing: boolean, setEditing: Function, selectedPrint: PrintTemplateType }) {

  const { openModal, popUp, hierarchyLabel } = useContext(UserContext);
  const { t } = useTranslation('translation');
  const [printPreviewModal, setPrintPreviewModal] = useState(false);
  const [showCustomer, setShowCustomer] = useState(false);
  const [showHeader, setShowHeader] = useState(false);
  const [showFooter, setShowFooter] = useState(false);
  const [showColor, setShowColor] = useState(selectedPrint && (selectedPrint.logo || selectedPrint.color) ? true : false);
  const [canShow, setCanShow] = useState(true);

  const [printTemplate, setPrintTemplate] = useState<PrintTemplateType | null>(editing ? selectedPrint : initialState);

  useEffect(() => {
    if (editing && selectedPrint) {
      selectedPrint.cliente?.length && setShowCustomer(true);
      selectedPrint.color?.length && setShowColor(true);
      if (selectedPrint.headerText?.bold
        || selectedPrint.headerText?.italic
        || selectedPrint.headerText?.size
        || selectedPrint.headerText?.text) {
        setShowHeader(true);
      }
      if (selectedPrint.footerText?.bold
        || selectedPrint.footerText?.italic
        || selectedPrint.footerText?.size
        || selectedPrint.footerText?.text) {
        setShowFooter(true);
      }
      setPrintTemplate({ ...selectedPrint });
    }
  }, []);

  const results = queryFn.useFetch('GET_CLIENTES', async () => {
    try {
      const token = await AttToken();
      if (token) {
        const body = { ...token, empresa: token.userEmpresa };
        const result = await customerService.getAllClients(body, true);
        return result.clientList;
      }
    } catch (err) {
      console.error(err);
    }
  });

  const create = queryFn.useMutate('CREATE_TEMPLATE', async () => {
    try {
      const token = await AttToken();
      if (token) {
        await printService.createPrintTemplate(token, { ...printTemplate as printTemplate, logo: printTemplate?.logo || null });
        setPrintTemplate(null);
        queryClient.resetQueries({ queryKey: ['GET_ALL_PRINTS'] });
        toast.success(`${t('toast.templateCreated')}`);
      }
    } catch (err) {
      console.error(err);
    }
  });

  const edit = queryFn.useMutate('EDIT_TEMPLATE', async () => {
    try {
      toast.warn(`${t('toast.savingEdit')}`);
      const token = await AttToken();
      if (token) {
        await printService.editPrintTemplate(token, {
          ...printTemplate as PrintTemplateType,
          logo: printTemplate?.logo || undefined, empresa: undefined
        }, selectedPrint.id as string);
        queryClient.resetQueries({ queryKey: ['GET_ALL_PRINTS'] });
        setEditing(false);
        toast.success(`${t('toast.templateEdited')}`);
        openModal();
      }
    } catch (err) {
      console.error(err);
    }
  });

  function getBase64(file: File, cb: (s: string) => void) {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      cb(reader.result as string);
    };
    reader.onerror = function (error) {
      console.log('Error: ', error);
    };
  }

  const renderImages = (File: FileList | null) => {
    if (File) {
      Array.from(File).map(e => getBase64(e, (i) => setPrintTemplate({ ...printTemplate as PrintTemplateType, logo: i })));
    }
  };

  const close = () => {
    trackEventMatomo('PrintTemplateModal', 'click', 'button', 'cancela');
    openModal();
    setEditing(false);
    setPrintTemplate(null);
  };

  const escape = ((e: any): void => {
    if (e.key === 'Escape') {
      close();
    }
  });

  useEffect(() => {
    document.addEventListener('keydown', escape, true);
  }, [escape]);

  const submit = () => {
    trackEventMatomo('PrintTemplateModal', 'click', 'button', 'salva');
    !editing
      ? create.mutateAsync('')
      : edit.mutateAsync('');
  };

  return (
    <>
      {printPreviewModal && !canShow &&
        <PrintPreviewCreateModal
          printTemplate={printTemplate as PrintTemplateType}
          image={String(printTemplate?.logo)}
          printPreviewModal={printPreviewModal}
          setPrintPreviewModal={setPrintPreviewModal}
          setCanShow={setCanShow}
        />}
      {canShow
        && <div className={`modal ${popUp ? "modal is-active" : "modal"}`}>
          <div className="modal-background" onKeyDown={(e) => escape(e)} onClick={close} />
          <ModalCard className="modal-card" onSubmit={(e: React.FormEvent) => e.preventDefault()}>
            <HeaderModal headerTitle={t('checklists.printConfig.configPrintTemplate')} close={close} />
            <ModalCardbody className="modal-card-body">
              <PrintTemplateModalContainer>
                <TextField
                  label={t('checklists.printConfig.templateName')}
                  fullWidth
                  value={printTemplate?.name}
                  onClick={() => trackEventMatomo('PrintTemplateModal', 'click', 'input', 'edita valor')}
                  onChange={(e: any) => {
                    setPrintTemplate({ ...printTemplate as PrintTemplateType, name: e.target.value });
                  }}
                />
                <CheckboxContainer>
                  <Label className='checkbox'>
                    <input
                      type="checkbox"
                      checked={printTemplate?.dafaultTemplate}
                      onChange={() => {
                        trackEventMatomo('PrintTemplateModal', 'click', 'input', 'habilita valor');
                        setPrintTemplate({ ...printTemplate as PrintTemplateType, dafaultTemplate: !printTemplate?.dafaultTemplate });
                      }}
                    />
                    {t('checklists.printConfig.setDefault')}
                  </Label>
                </CheckboxContainer>
                <CheckboxContainer>
                  <Label className='checkbox'>
                    <input
                      type="checkbox"
                      checked={showCustomer}
                      onChange={(e) => {
                        trackEventMatomo('PrintTemplateModal', 'click', 'input', 'habilita valor');
                        if (!e.target.checked) {
                          delete selectedPrint.cliente;
                          setPrintTemplate({ ...selectedPrint });
                        }
                        setShowCustomer(e.target.checked);
                      }}
                    />
                    {t('checklists.printConfig.customerLimit')}
                  </Label>
                  {
                    showCustomer && (
                      results.isLoading ? <Skeleton variant='rounded' height={40} /> :
                        <Autocomplete
                          value={printTemplate?.cliente ? {
                            value: printTemplate?.cliente,
                            label: results.data?.find((ele: ClientType) => ele.id === printTemplate?.cliente)?.nome
                          } : null}
                          id="combo-box-demo"
                          sx={{ marginTop: '10px' }}
                          isOptionEqualToValue={(option, value) => option.value === value.value}
                          filterSelectedOptions
                          options={
                            results.data
                              ?.sort((a: ClientType, b: ClientType) => { return a.nome > b.nome ? 1 : - 1; })
                              ?.filter((e: ClientType) => e.id !== '')
                              ?.map((ele: any) => ({
                                label: ele.nome,
                                value: ele.id,
                              }))}
                          onClick={() => trackEventMatomo('PrintTemplateModal', 'click', 'select', 'seleciona valor')}
                          onChange={(event: any, newValue: { label: string, value: string } | null) => {
                            setPrintTemplate({ ...printTemplate as PrintTemplateType, cliente: newValue?.value });
                          }}
                          renderOption={(props, option, { selected }) => (
                            <li {...props} key={option.value}>
                              <span>{option.label}</span>
                            </li>)}
                          renderInput={(params) => <TextField {...params} label={hierarchyLabel.cliente} />}
                        />
                    )}
                </CheckboxContainer>
                <CheckboxContainer>
                  <Label className='checkbox'>
                    <input
                      type="checkbox"
                      checked={showHeader}
                      onChange={(event) => {
                        trackEventMatomo('PrintTemplateModal', 'click', 'checkbox', 'habilita valor');
                        if (!event.target.checked) {
                          delete selectedPrint.headerText;
                          setPrintTemplate({ ...selectedPrint });
                        }
                        setShowHeader(event.target.checked);
                      }}
                    />
                    {t('checklists.printConfig.headerText')}
                  </Label>
                  {
                    showHeader && (
                      <>
                        <textarea
                          className='textarea is-small is-success'
                          rows={2}
                          value={printTemplate?.headerText?.text}
                          onClick={() => trackEventMatomo('PrintTemplateModal', 'click', 'textarea', 'edita valor')}
                          onChange={(e) => {
                            setPrintTemplate({
                              ...printTemplate as PrintTemplateType,
                              headerText: {
                                ...printTemplate?.headerText,
                                text: e.target.value
                              }
                            });
                          }}
                          style={{
                            fontSize: printTemplate?.headerText?.size,
                            fontWeight: printTemplate?.headerText?.bold ? 'bold' : 500,
                            fontStyle: printTemplate?.headerText?.italic ? 'italic' : 'normal'
                          }}
                        />
                        <ResizeContainer>
                          <span>{t('checklists.printConfig.fontSize')}</span>
                          <Input
                            className='input is-small'
                            type="number"
                            onClick={() => trackEventMatomo('PrintTemplateModal', 'click', 'input', 'edita valor')}
                            onChange={(e: any) => {
                              setPrintTemplate({
                                ...printTemplate as PrintTemplateType,
                                headerText: {
                                  ...printTemplate?.headerText,
                                  size: Number(e.target.value)
                                }
                              });
                            }}
                            value={printTemplate?.headerText?.size}
                          />
                          <button
                            className={printTemplate?.headerText?.bold ? 'marked' : ''}
                            onClick={() => {
                              trackEventMatomo('PrintTemplateModal', 'click', 'button', 'negrito');
                              setPrintTemplate({
                                ...printTemplate as PrintTemplateType,
                                headerText: {
                                  ...printTemplate?.headerText,
                                  bold: !printTemplate?.headerText?.bold
                                }
                              });
                            }}
                          >
                            <FaBold />
                          </button>
                          <button
                            className={printTemplate?.headerText?.italic ? 'marked' : ''}
                            onClick={() => {
                              trackEventMatomo('PrintTemplateModal', 'click', 'button', 'italico');
                              setPrintTemplate({
                                ...printTemplate as PrintTemplateType,
                                headerText: {
                                  ...printTemplate?.headerText,
                                  italic: !printTemplate?.headerText?.italic
                                }
                              });
                            }}
                          >
                            <FaItalic />
                          </button>
                        </ResizeContainer>
                      </>
                    )}
                </CheckboxContainer>
                <CheckboxContainer>
                  <Label className='checkbox'>
                    <input
                      type="checkbox"
                      checked={showFooter}
                      onChange={(event) => {
                        trackEventMatomo('PrintTemplateModal', 'click', 'checkbox', 'habilita valor');
                        if (!event.target.checked) {
                          delete selectedPrint.footerText;
                          setPrintTemplate({ ...selectedPrint });
                        }
                        setShowFooter(event.target.checked);
                      }}
                    />
                    {t('checklists.printConfig.footerText')}
                  </Label>
                  {showFooter && (
                    <>
                      <textarea
                        className='textarea is-small is-success'
                        rows={2}
                        value={printTemplate?.footerText?.text}
                        onClick={() => trackEventMatomo('PrintTemplateModal', 'click', 'textarea', 'edita valor')}
                        onChange={(e) => {
                          setPrintTemplate({
                            ...printTemplate as PrintTemplateType,
                            footerText: {
                              ...printTemplate?.footerText,
                              text: e.target.value
                            }
                          });
                        }}
                        style={{
                          fontSize: printTemplate?.footerText?.size,
                          fontWeight: printTemplate?.footerText?.bold ? 'bold' : 500,
                          fontStyle: printTemplate?.footerText?.italic ? 'italic' : 'normal'
                        }}
                      />
                      <ResizeContainer>
                        <span>{t('checklists.printConfig.fontSize')}</span>
                        <Input
                          className='input is-small'
                          type="number"
                          onClick={() => trackEventMatomo('PrintTemplateModal', 'click', 'input', 'edita valor')}
                          onChange={(e: any) => {
                            setPrintTemplate({
                              ...printTemplate as PrintTemplateType,
                              footerText: {
                                ...printTemplate?.footerText,
                                size: Number(e.target.value)
                              }
                            });
                          }}
                          value={printTemplate?.footerText?.size}
                        />
                        <button
                          className={printTemplate?.footerText?.bold ? 'marked' : ''}
                          onClick={() => {
                            trackEventMatomo('PrintTemplateModal', 'click', 'button', 'negrito');
                            setPrintTemplate({
                              ...printTemplate as PrintTemplateType,
                              footerText: {
                                ...printTemplate?.footerText,
                                bold: !printTemplate?.footerText?.bold
                              }
                            });
                          }}
                        >
                          <FaBold />
                        </button>
                        <button
                          className={printTemplate?.footerText?.italic ? 'marked' : ''}
                          onClick={() => {
                            trackEventMatomo('PrintTemplateModal', 'click', 'button', 'italico');
                            setPrintTemplate({
                              ...printTemplate as PrintTemplateType,
                              footerText: {
                                ...printTemplate?.footerText,
                                italic: !printTemplate?.footerText?.italic
                              }
                            });
                          }}
                        >
                          <FaItalic />
                        </button>
                      </ResizeContainer>
                    </>
                  )}
                </CheckboxContainer>
                <CheckboxContainer>
                  <Label className='checkbox'>
                    <input
                      type="checkbox"
                      checked={showColor}
                      onChange={(event) => {
                        trackEventMatomo('PrintTemplateModal', 'click', 'input', 'edita valor');
                        if (!event.target.checked) {
                          selectedPrint.color = undefined;
                          selectedPrint.logo = undefined;
                          setPrintTemplate({ ...selectedPrint });
                        }
                        setShowColor(event.target.checked);
                      }}
                    />
                    {t('checklists.printConfig.graphicSettings')}
                  </Label>
                  {showColor && (
                    <div style={{ display: 'flex', marginTop: '20px', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', width: '220px', justifyContent: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span style={{ fontSize: '15px', fontWeight: 'bold' }}>{t('checklists.printConfig.backgroundColor')}</span>
                          <span>
                            <PreviewColor
                              style={{ backgroundColor: printTemplate?.color }}
                              onClick={() => {
                                trackEventMatomo('PrintTemplateModal', 'click', 'input', 'edita valor');
                                setPrintTemplate({ ...printTemplate as PrintTemplateType, color: printTemplate?.color });
                              }}
                            />
                          </span>
                        </div>
                        {showColor && (
                          <SketchPicker
                            color={printTemplate?.color}
                            onChangeComplete={
                              (color: ColorResult) => {
                                trackEventMatomo('PrintTemplateModal', 'click', 'input', 'edita valor');
                                setPrintTemplate({ ...printTemplate as PrintTemplateType, color: color.hex });
                              }
                            }
                            disableAlpha
                            presetColors={['#fff', '#000', '#f00', '#0f0', '#00f']}
                          />
                        )}
                      </div>
                      <DropzoneContainer>
                        <span>{t('checklists.printConfig.headerPicture')}</span>
                        {printTemplate?.logo && <img alt="Logo da empresa" src={printTemplate?.logo} width={'95%'} />}
                        <span className='button is-success'>
                          <input
                            accept="image/png, image/jpeg"
                            value={undefined}
                            id="img"
                            name="img"
                            multiple={true}
                            className="file-input"
                            type="file"
                            onChange={(e) => {
                              trackEventMatomo('PrintTemplateModal', 'click', 'input', 'edita imagem');
                              renderImages(e.target.files);
                            }}
                          />
                          {t('users.userRegister.selectMessage')}
                          <AddPhotoAlternateIcon />
                        </span>
                      </DropzoneContainer>
                    </div>
                  )}
                </CheckboxContainer>
              </PrintTemplateModalContainer>
              <FormControl fullWidth>
                <InputLabel id="demo-simple-select-label">{t('status')}</InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  label={t('status')}
                  onClick={() => trackEventMatomo('PrintTemplateModal', 'click', 'select', 'seleciona valor')}
                  onChange={(e) => {
                    setPrintTemplate({ ...printTemplate as PrintTemplateType, status: e.target.value === 'ativo' ? true : false });
                  }}
                  defaultValue={printTemplate?.status ? 'ativo' : 'inativo'}
                >
                  <MenuItem value={'ativo'}>{t('active')}</MenuItem>
                  <MenuItem value={'inativo'}>{t('inactive')}</MenuItem>
                </Select>
              </FormControl>
              <div>
                <Button
                  endIcon={<VisibilityIcon />}
                  sx={{ marginTop: '10px' }}
                  className='button is-light is-small'
                  onClick={() => {
                    trackEventMatomo('PrintTemplateModal', 'click', 'button', 'abre modal');
                    setPrintPreviewModal(true);
                    setCanShow(false);
                  }}
                >
                  {t('checklists.printConfig.preview')}
                </Button>
              </div>
            </ModalCardbody>
            <FooterModal
              submit={submit}
              disabled={create.isPending || edit.isPending}
              label={t('save')}
            />
          </ModalCard>
        </div >}
    </>
  );
}
